import { IAppState } from '../../reducers/index';
import { ActionType, ActionTypes } from '../../actions/index';
import { CartSeat } from '../../classes/seat';
import { postCheckoutCart, postQuickCheckout, postCheckQuickCheckout } from '../actions/cart-fetch';
import { selectSeat, loadInCartStyleForSeat, unlockSeats, lockSeats, openSeatmapCart } from '../../seatmap/actions/seatmap-actions';
import { getCurrentLanguage } from '../../helpers/window-helpers';
import { deleteSeatRequest } from '../../helpers/local-storage-helpers';

import { createFilterRequest } from '../../actions/fetch';
import { ISeatMatchRequestModel } from '../../classes/request-model';
import { orderBy, uniq, groupBy, each } from 'lodash';
import { ItemType } from '../../classes/item-type';
import { IExchangeTicket } from '../../classes/ticket-exchange';
import { setActiveExchangeTickets } from '../../actions/exchange-actions';
import { setPriceSettings } from '../../actions/settings-fetch';
import { PriceType } from '../../classes/price-type';
import { CartHelpers } from '../../helpers/cart-helpers';
import { SeatingOptionGroup } from '../../classes/seating-option';

/*
 * action creators
 */

export interface IMaxTicketsErrorActionValue {
    isError: boolean;
    priceTypeText: string;
    isPriceType: boolean;
}

// could be seating option as well
export function maxTicketsThresholdReached(isPriceType: boolean, priceTypeText: string) {
    return (dispatch, getState: () => IAppState) => {
        const state = getState();
        if (CartHelpers.isSetQuantityPackage(state.appSettingsModel)) {
            //flex max is being handled elsewhere
            return;
        }
        const val: IMaxTicketsErrorActionValue = {
            isError: true,
            priceTypeText: priceTypeText,
            isPriceType: isPriceType
        }
        dispatch({ type: ActionTypes.MAX_TICKETS_SELECTED, value: val });
    }
}

export function resetmaxTicketsThreshold() {
    const val: IMaxTicketsErrorActionValue = {
        isError: false,
        priceTypeText: "",
        isPriceType: null
    }
    return { type: ActionTypes.MAX_TICKETS_SELECTED, value: val }
}

export function flexTicketsThresholdNotReached() {
    return { type: ActionTypes.FLEX_TICKETS_NOT_SELECTED, value: true }
}

export function flexMinQuantityReached() {
    return { type: ActionTypes.FLEX_MIN_SEATS_SELECTED, value: true }
}

export function resetflexMinQuantityReached() {
    return { type: ActionTypes.FLEX_MIN_SEATS_SELECTED, value: false }
}

export function flexMaxQuantityReachedForAllTickets() {
    return { type: ActionTypes.FLEX_MAX_ALL_SEATS_SELECTED, value: true }
}

export function resetflexMaxQuantityReachedForAllTickets() {
    return { type: ActionTypes.FLEX_MAX_ALL_SEATS_SELECTED, value: false }
}

export function flexMaxQuantityReachedForPriceType() {
    return { type: ActionTypes.FLEX_MAX_SEATS_SELECTED_FOR_PRICETYPE, value: true }
}

export function resetflexMaxQuantityReachedForPriceType() {
    return { type: ActionTypes.FLEX_MAX_SEATS_SELECTED_FOR_PRICETYPE, value: false }
}

export function resetflexTicketsThreshold() {
    return { type: ActionTypes.FLEX_TICKETS_NOT_SELECTED, value: false }
}

export function addSeatToCart(seat: CartSeat, itemId: number, brandId: string, priceTypeSettings: Array<PriceType>, useOnlyPromoPriceType: boolean, activeExchangeTickets: Array<IExchangeTicket>, totalExchangeTickets: Array<IExchangeTicket>) {
    return (dispatch, getState) => {
        const state = getState() as IAppState;
        dispatch(loadInCartStyleForSeat(seat));
        seat.selectedPricing = { ...seat.selectedPricing, priceCarted: seat.selectedPricing.price };
        const newSeat = { ...seat };

        dispatch({ type: ActionTypes.ADD_SEAT_TO_CART, value: newSeat });
        // Open cart after success
        dispatch(openSeatmapCart());
        if (state.appSettingsModel.itemType === ItemType.Exchange) {
            dispatch(updateExchangeMessaging(state.cartModel.seats.concat(seat)));
        }

        if (seat.seatingOptionGroup != null) {
            const unlockableSeats = state.seatMapModel.levelSeats.filter(s => s.seatingOptionGroup != null
                && s.seatingOptionGroup.unlockSeatingOptionGroupId !== null);

            const seatsToUnlock = unlockableSeats.filter(s =>
                seat.seatingOptionGroup.id === s.seatingOptionGroup.unlockSeatingOptionGroupId);
            if (seatsToUnlock.length > 0) {
                dispatch(unlockSeats(seatsToUnlock));
            }
        }

        let newState = getState() as IAppState;
        let currentCart = newState.cartModel.seats;
        if (totalExchangeTickets.length > 0 && currentCart.length !== totalExchangeTickets.length) {
            //exchange mode
            let cartPrices = uniq(currentCart.map(cartSeat => cartSeat.selectedPricing.defaultPrice));

            // we met the quantity for the first price point, and we need to switch price points
            if (currentCart.filter(cartSeat => Math.max((cartSeat.selectedPricing.defaultPrice - activeExchangeTickets[0].oldPrice), 0) === cartSeat.selectedPricing.price).length === activeExchangeTickets.length) {

                let exchangeTicketsLeft = [];
                const exchangeTicketsGrouped = groupBy(totalExchangeTickets, (ticket: IExchangeTicket) => ticket.oldPrice);
                each(exchangeTicketsGrouped, (priceGrouped: Array<any>) => {
                    var inCartForPrice = currentCart.filter(cartSeat => Math.max((cartSeat.selectedPricing.defaultPrice - priceGrouped[0].oldPrice), 0) === cartSeat.selectedPricing.priceCarted)
                    if (inCartForPrice.length !== priceGrouped.length) {
                        exchangeTicketsLeft = exchangeTicketsLeft.concat(priceGrouped);
                    }
                })

                if (exchangeTicketsLeft.length > 0) {
                    var remainingExchangeTicketsGrouped = groupBy(exchangeTicketsLeft, (ticket: IExchangeTicket) => ticket.oldPrice);
                    const newActiveExchangeTickets = remainingExchangeTicketsGrouped[Object.keys(remainingExchangeTicketsGrouped)[0]];
                    dispatch(setActiveExchangeTickets(newActiveExchangeTickets, currentCart));
                    dispatch(setPriceSettings(priceTypeSettings, false, useOnlyPromoPriceType, newActiveExchangeTickets));
                }
            }
        }
    };
}

export function saveSeatRemoval(seat: CartSeat) {
    return { type: ActionTypes.SAVE_SEAT_REMOVAL_FROM_CART, value: seat };
}

export function removeSeatFromCart(seat: CartSeat, selectedPriceTypes: Array<PriceType>, brandId: string, confirm: boolean) {
    const hasSpecialSeating = seat.seatingOptionGroup !== null;

    if (confirm || !hasSpecialSeating) {
        return (dispatch, getState) => {
            // If we removed a seat, we're not a max
            dispatch(resetflexMaxQuantityReachedForAllTickets());
            dispatch(resetflexMaxQuantityReachedForPriceType());
            dispatch({ type: ActionTypes.REMOVE_SEAT_INCART_STYLE, value: seat });
            dispatch({ type: ActionTypes.REMOVE_SEAT_FROM_CART, value: seat });

            const state = getState() as IAppState;

            if (CartHelpers.isSetQuantityPackage(state.appSettingsModel)) {
                dispatch(disableCart(true))
            }
            const globalMin = state.appSettingsModel.minTicketQuantity;

            if (state.appSettingsModel.itemType === ItemType.Super || state.appSettingsModel.itemType === ItemType.Flex || globalMin > 0) {
                // Here is the first location we know the price type, so we're gonna use it up update the display
                dispatch(updateFlexMessaging(+(seat.selectedPricing.priceTypeId), selectedPriceTypes));
            }
            if (state.appSettingsModel.itemType === ItemType.Exchange) {
                dispatch(updateExchangeMessaging(state.cartModel.seats.filter(s => s.seatId !== seat.seatId)));
            }

            if (hasSpecialSeating) {
                const unlockableSeats = state.seatMapModel.levelSeats.filter(s => s.seatingOptionGroup !== null
                    && s.seatingOptionGroup.unlockSeatingOptionGroupId !== null);

                const seatsToLock = unlockableSeats.filter(s =>
                    seat.seatingOptionGroup.id === s.seatingOptionGroup.unlockSeatingOptionGroupId);

                if (seatsToLock.length > 0) {
                    var matchedSeats = seatsToLock.filter(seatLock => state.cartModel.seats.some(s => s.seatId === seatLock.seatId));

                    matchedSeats.forEach(matchedSeat => {
                        dispatch({ type: ActionTypes.REMOVE_SEAT_INCART_STYLE, value: matchedSeat });
                        dispatch({ type: ActionTypes.REMOVE_SEAT_FROM_CART, value: matchedSeat });
                    });
                    dispatch(lockSeats(seatsToLock));
                }

            }
            //dispatch(fetchRemoveInCartSeat(itemId, seat.tessituraSeat.seatNumber, seat.seatId, brandId));
        };
    } else {
        return { type: ActionTypes.SHOW_CART_REMOVAL_MODAL, value: true }
    }
}

export function closeCartRemovalModal() {
    return { type: ActionTypes.SHOW_CART_REMOVAL_MODAL, value: false };
}

export function setCartUpdated(cartUpdated: boolean) {
    return { type: ActionTypes.CART_UPDATED, value: cartUpdated };
}

export function removeAllSeatsFromCart(itemId: number) {
    return (dispatch, state) => {
        let currentState: IAppState = state();

        currentState.cartModel.seats.forEach(seat => {
            dispatch({ type: ActionTypes.REMOVE_SEAT_INCART_STYLE, value: seat });
            dispatch({ type: ActionTypes.REMOVE_SEAT_FROM_CART, value: seat });
            //removeCartSeat(itemId, seat.tessituraSeat.seatNumber, seat.seatId);
        });
    };
}

export function disableCart(disable: boolean) {
    return { type: ActionTypes.DISABLE_CART, value: disable };
}

export function checkoutCart(seats: Array<CartSeat>, selectedAdditionalOptionGroups: Array<SeatingOptionGroup>, additionalNotes: string, itemId: number, itemType: ItemType, language: string, proceedToAddons: boolean) {
    return (dispatch, state) => {
        dispatch({ type: ActionTypes.RESERVE_CART });
        dispatch(postCheckoutCart(itemId, itemType, seats, selectedAdditionalOptionGroups, additionalNotes, language, proceedToAddons));

        const appSettings = (state() as IAppState).appSettingsModel;
        deleteSeatRequest(appSettings.itemId, appSettings.itemType);
    };
}

export function tryCheckout(cartSeats: Array<CartSeat>, selectedAdditionalOptionGroups: Array<SeatingOptionGroup>, additionalNotes: string, itemId: number, proceedToAddons: boolean) {
    return (dispatch: any, getState: () => IAppState) => {
        const state = getState();
        let cartPriceTypeIds: Array<number> = uniq(cartSeats.map(cartSeat => cartSeat.selectedPricing.priceTypeId));

        let flexPackagePriceTypeError = false;
        cartPriceTypeIds.forEach(priceTypeId => {
            const samePriceTypeSeatsInCart = cartSeats.filter(cartSeat => cartSeat.selectedPricing.priceTypeId === priceTypeId);
            if (CartHelpers.isSetQuantityPackage(state.appSettingsModel)) {
                const samePriceTypeFlexPackageQuantity = state.priceTypesModel.priceTypes
                    .filter(pt => pt.priceTypeId === priceTypeId);

                if (samePriceTypeFlexPackageQuantity.length > 0
                    && samePriceTypeFlexPackageQuantity[0].selectedQuantity !== samePriceTypeSeatsInCart.length) {
                    flexPackagePriceTypeError = true;
                }
            }
        });

        if (flexPackagePriceTypeError) {
            dispatch(flexTicketsThresholdNotReached());
        } else {
            dispatch(checkoutCart(cartSeats, selectedAdditionalOptionGroups, additionalNotes, itemId, state.appSettingsModel.itemType, getCurrentLanguage(), proceedToAddons));
        }
    }
}

export function updateFlexMessaging(selectedPriceTypeId: number, allPriceTypes: Array<PriceType>) {
    return (dispatch, getState: () => IAppState) => {

        const seats = getState().cartModel.seats;
        var priceTypeMaxReached = false;
        var allPriceTypeMaxReached = true;

        allPriceTypes.forEach(priceType => {
            let priceTypeSeats = seats.filter(s => s.selectedPricing.priceTypeId === priceType.priceTypeId);
            var hitLimit = priceTypeSeats.length >= priceType.selectedQuantity;
            if (hitLimit) {
                if (selectedPriceTypeId === priceType.priceTypeId)
                    priceTypeMaxReached = true;
            } else {
                allPriceTypeMaxReached = false;
            }
        })

        if (allPriceTypeMaxReached) {
            // If we have the max seats for a price type, display that
            dispatch(flexMaxQuantityReachedForAllTickets());
            dispatch(flexMaxQuantityReachedForPriceType());
            dispatch(resetflexMinQuantityReached())
            dispatch(disableCart(!CartHelpers.isFlexCartFull(getState())));
        }
        else if (priceTypeMaxReached) {
            dispatch(flexMaxQuantityReachedForPriceType());
            dispatch(flexMinQuantityReached());
            dispatch(disableCart(!CartHelpers.isFlexCartFull(getState())));
        } else {
            // Otherwise prompt for more
            dispatch(flexMinQuantityReached());
            dispatch(resetflexMaxQuantityReachedForAllTickets());
            dispatch(resetflexMaxQuantityReachedForPriceType());
            dispatch(disableCart(true));
        }
    }
}

export function updateExchangeMessaging(newSeats: Array<CartSeat>) {
    return (dispatch, getState: () => IAppState) => {
        const state = getState();
        if (newSeats.length === state.appSettingsModel.exchangeTickets.length) {
            dispatch(disableCart(false));
        } else {
            dispatch(disableCart(true));
        }
    }
}

export function confirmQuickCheckoutAndComplete() {
    return (dispatch, state) => {
        let currentState: IAppState = state();
        const seatMatchRequest = createFilterRequest(currentState);

        dispatch({ type: ActionTypes.QUICK_CHECKOUT });
        dispatch(postCheckQuickCheckout(seatMatchRequest));

        const appSettings = (state() as IAppState).appSettingsModel;
        deleteSeatRequest(appSettings.itemId, appSettings.itemType);
    }
}

export function quickCheckout() {
    return (dispatch, state) => {
        let currentState: IAppState = state();
        const seatMatchRequest = createFilterRequest(currentState);

        dispatch({ type: ActionTypes.QUICK_CHECKOUT });
        dispatch(postQuickCheckout(seatMatchRequest));

        const appSettings = (state() as IAppState).appSettingsModel;
        deleteSeatRequest(appSettings.itemId, appSettings.itemType);
    }
}

export function backToFilters() {
    return {
        type: ActionTypes.GO_TO_FILTERS
    };
}

export function setCartTimer(remainingMinutes: number) {
    return {
        type: ActionTypes.SET_CART_TIMER,
        value: remainingMinutes
    };
}
